import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { createSessionThunk, updateSessionThunk } from '../redux/academicCalendarSlice'; // Import updateSessionThunk
import { setStepValid, startTour, stopTour } from '../../../redux/globalJoyrideSlice';
import { getCelebrationsTourSteps } from '../../../tour/academicCalenderTourSteps';

const AddSessionForm = ({ onClose, editItem,
  setActiveTab,
  setTourStage,
  setCelebrationSubTab,
  setShowAddCelebrationForm,
  setAwarenessSubTab,
}) => { // Accept editItem prop
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [termOneStart, setTermOneStart] = useState('');
  const [termOneEnd, setTermOneEnd] = useState('');
  const [termTwoStart, setTermTwoStart] = useState('');
  const [termTwoEnd, setTermTwoEnd] = useState('');

  const dispatch = useDispatch();

  useEffect(() => {
    // If editItem is provided, populate the form fields
    if (editItem) {
      setStartDate(moment(editItem.start_date).format('YYYY-MM-DD'));
      setEndDate(moment(editItem.end_date).format('YYYY-MM-DD'));
      setTermOneStart(
        editItem.term_one_start_date
          ? moment(editItem.term_one_start_date).format('YYYY-MM-DD')
          : ''
      );

      setTermOneEnd(
        editItem.term_one_end_date
          ? moment(editItem.term_one_end_date).format('YYYY-MM-DD')
          : ''
      );

      setTermTwoStart(
        editItem.term_two_start_date
          ? moment(editItem.term_two_start_date).format('YYYY-MM-DD')
          : ''
      );

      setTermTwoEnd(
        editItem.term_two_end_date
          ? moment(editItem.term_two_end_date).format('YYYY-MM-DD')
          : ''
      );
    }
  }, [editItem]); // Re-run effect if editItem changes

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    if (startMoment.isAfter(endMoment)) {
      alert('Start date cannot be after end date.');
      return;
    }

    const sessionData = {
      school_id: 1, // Ensure these are dynamic if needed
      user_id: 1,   // Ensure these are dynamic if needed
      start_date: startMoment.format('YYYY-MM-DD'),
      end_date: endMoment.format('YYYY-MM-DD'),
      term_one_start_date: termOneStart || null,
      term_one_end_date: termOneEnd || null,

      term_two_start_date: termTwoStart || null,
      term_two_end_date: termTwoEnd || null,
      details: `Academic session from ${startMoment.format('DD MMMMYYYY')} to ${endMoment.format('DD MMMMYYYY')}`,
      status: 'active',
    };

    try {
      if (editItem) {
        // If editItem exists, update the session
        await dispatch(updateSessionThunk({ id: editItem.originalId || editItem.id, sessionData })).unwrap();
        alert('Session dates updated successfully!');
      } else {
        // Otherwise, create a new session
        await dispatch(createSessionThunk(sessionData)).unwrap();
        alert('Session dates saved successfully!');
      }
      dispatch(stopTour());

      // 2️⃣ Switch UI to Celebrations
      setActiveTab("celebrations");
      setTourStage("celebrations");
      setCelebrationSubTab("proposed");

      // 3️⃣ Start Celebrations tour after DOM renders
      setTimeout(() => {
        dispatch(
          startTour({
            tourName: "celebrations",
            steps: getCelebrationsTourSteps({
              dispatch,
              setCelebrationSubTab,
              setShowAddCelebrationForm,
              setActiveTab,
              setTourStage,
              setAwarenessSubTab,
            }),
          })
        );
      }, 300);
    } catch (error) {
      console.error('Failed to save session dates:', error);
      alert(`Error saving session dates: ${error.message || 'Please try again.'}`);
    }

    // Clear form fields after submission
    setStartDate('');
    setEndDate('');
    setTermOneStart('');
    setTermOneEnd('');
    setTermTwoStart('');
    setTermTwoEnd('');
  };

  return (
    <div className="container-fluid">
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="startDate" className="form-label">Session Start Date:</label>
          <input
            type="date"
            id="startDate"
            className="form-control"
            data-tour="session-start-date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              dispatch(setStepValid(true));
            }
            }
            required
          />
        </div>
        <div className="mb-3">
          <label htmlFor="endDate" className="form-label">Session End Date:</label>
          <input
            type="date"
            id="endDate"
            className="form-control"
            data-tour="session-end-date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              dispatch(setStepValid(true));
            }}
            required
          />
        </div>
        {/* TERM 1 */}
        <div className="mb-3">
          <label className="form-label">Term 1 Start Date</label>
          <input
            type="date"
            className="form-control"
            data-tour="term1-start-date"
            value={termOneStart}
            onChange={(e) => {
              setTermOneStart(e.target.value);
              dispatch(setStepValid(true));
            }}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Term 1 End Date</label>
          <input
            type="date"
            className="form-control"
            data-tour="term1-end-date"
            value={termOneEnd}
            onChange={(e) => {
              setTermOneEnd(e.target.value);
              dispatch(setStepValid(true));
            }}
          />
        </div>

        {/* TERM 2 */}
        <div className="mb-3">
          <label className="form-label">Term 2 Start Date</label>
          <input
            type="date"
            className="form-control"
            data-tour="term2-start-date"
            value={termTwoStart}
            onChange={(e) => {
              setTermTwoStart(e.target.value);
              dispatch(setStepValid(true));
            }}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Term 2 End Date</label>
          <input
            type="date"
            className="form-control"
            data-tour="term2-end-date"
            value={termTwoEnd}
            onChange={(e) => {
              setTermTwoEnd(e.target.value);
              dispatch(setStepValid(true));
            }}
          />
        </div>
        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" data-tour="save-session">
            {editItem ? "Update Session Dates" : "Save Session Dates"}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddSessionForm;